import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { type StandardsFilterState } from './models/filterDTOs';
import type {
    StandardsReportSummaryDTO,
    StudentStandardsSummaryDTO,
} from './models/standardsReportDTOs';
import StandardsReportFilters from './components/StandardsReportFilters';
import StandardsReportTable from './components/StandardsReportTable';
import StandardsStudentTable from './components/StandardsReportStudentTable';
import { useAuth } from '@/src/app/context/useAuth';

const StandardsReportPage: React.FC = () => {
    document.title = 'Standards Report';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [examName, setExamName] = useState(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) return JSON.parse(saved)?.examName || '';
        } catch {
            // intentionally empty
        }
        return '';
    });
    const pendingExamNameRef = useRef('');

    const [filterState, setFilterState] = useState<StandardsFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                    studentId: 0,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
            studentId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
                examIds: [filterState.examId],
                examName,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, examName, userId, UNIVERSAL_FILTER_KEY]);

    const effectiveExamId = filterState.examId;

    const [rows, setRows] = useState<StandardsReportSummaryDTO[]>([]);
    const [studentRows, setStudentRows] = useState<StudentStandardsSummaryDTO[]>([]);
    const [studentLoaded, setStudentLoaded] = useState(false);
    const [studentError, setStudentError] = useState('');

    const refreshTableData = useCallback((state: StandardsFilterState) => {
        setError('');
        setLoaded(false);
        setHasSearched(true);

        apiRequest<StandardsReportSummaryDTO[]>(
            'GET',
            `reports/standardsReport?districtId=${state.districtId}&schoolId=${state.schoolId}&gradeLevelMasterId=${state.gradeLevelMasterId}&classId=${state.classId}&examTypeMasterId=${state.examTypeMasterId || 0}&examId=${state.examId}`
        )
            .then((res) => {
                if (Array.isArray(res.data)) setRows(res.data);
                else setRows([]);
                if (pendingExamNameRef.current) setExamName(pendingExamNameRef.current);
            })
            .catch(() => {
                setError('Failed to load Class Skill Summary');
                setRows([]);
            })
            .finally(() => setLoaded(true));
    }, []);

    const refreshStudentTable = useCallback((state: StandardsFilterState, studentId = 0) => {
        setStudentError('');
        setStudentLoaded(false);

        apiRequest<StudentStandardsSummaryDTO[]>(
            'GET',
            `reports/standardsReport/student?districtId=${state.districtId}&schoolId=${state.schoolId}&gradeLevelMasterId=${state.gradeLevelMasterId}&classId=${state.classId}&examTypeMasterId=${state.examTypeMasterId || 0}&examId=${state.examId}&studentId=${studentId}`
        )
            .then((res) => setStudentRows(Array.isArray(res.data) ? res.data : []))
            .catch(() => {
                setStudentError('Failed to load Student Skill Summary');
                setStudentRows([]);
            })
            .finally(() => setStudentLoaded(true));
    }, []);

    const emptyMessage = useMemo(
        () => (hasSearched ? 'No standards data found.' : ''),
        [hasSearched]
    );

    const refreshBoth = useCallback(
        (state: StandardsFilterState) => {
            refreshTableData(state);
            refreshStudentTable(state, Number(state.studentId) || 0);
        },
        [refreshTableData, refreshStudentTable]
    );

    const combinedLoading = hasSearched && (!loaded || !studentLoaded);
    const combinedError = error || studentError;

    useEffect(() => {
        const fs = filterState;
        if (
            fs.districtId &&
            fs.gradeLevelMasterId &&
            fs.gradeLevelMasterId &&
            fs.classId &&
            fs.examId
        ) {
            refreshBoth(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Standards Report</h1>
            <br />

            <StandardsReportFilters
                filterState={filterState}
                setFilterState={setFilterState}
                refreshTableData={refreshBoth}
                onExamNameChange={(name) => {
                    pendingExamNameRef.current = name;
                }}
            />

            {examName && (
                <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>
                    {examName}
                </h2>
            )}

            {hasSearched && (
                <>
                    <Loading visible={combinedLoading} message="Loading Standards Report..." />

                    {/* Show any error after loading finishes (or immediately if an error happens fast) */}
                    <ErrorMessage error={combinedError} />

                    <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                        {filterState.examId !== null && filterState.examId !== 0 && (
                            <div className="ml-auto">
                                <Button
                                    onClick={() => null}
                                    sx={{
                                        textTransform: 'none',
                                    }}
                                    variant="outlined"
                                    href={`/reports/questionList/${filterState.examId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    View Assessment
                                </Button>
                            </div>
                        )}
                    </Box>

                    {!combinedLoading && !combinedError && (
                        <>
                            <StandardsReportTable
                                data={rows}
                                isLoading={false}
                                error={error}
                                emptyMessage={emptyMessage}
                                examId={effectiveExamId}
                            />

                            <StandardsStudentTable
                                data={studentRows}
                                emptyMessage="No student data found."
                            />
                        </>
                    )}
                </>
            )}
        </Container>
    );
};

export default StandardsReportPage;
